import React, { useEffect, useState } from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Button from "react-bootstrap/Button";
import Table from "react-bootstrap/Table";
import Modal from "react-bootstrap/Modal";
import omsApi from "../../services/omsapi";
import { BiCloudDownload } from "react-icons/bi";
import { useParams } from "react-router-dom";
import moment from "moment/moment";
import jsPDF from "jspdf";
import "jspdf-autotable";
import logo from "../../static/base64img/logo"


function GenerateInvoice(invoiceDetails) {
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 40;
    const logoBase64 = logo.logo;

    const imgWidth = 60, imgHeight = 60;
    const imgX = (pageWidth - imgWidth) / 2, imgY = 10;

    doc.addImage(logoBase64, 'PNG', imgX, imgY, imgWidth, imgHeight);

    const nextY = imgY + imgHeight + 10;
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    const invoiceText = `Invoice #: ${invoiceDetails.order_showid}`;
    const dateText = `Date: ${moment(invoiceDetails.order_posteddate).format("YYYY/MM/DD HH:mm:ss")}`;
    const dateTextWidth = doc.getTextWidth(dateText);
    doc.text(invoiceText, margin, nextY);
    doc.text(dateText, pageWidth - margin - dateTextWidth, nextY);

    const billedTo = [
        invoiceDetails.shipping_name,
        invoiceDetails.shipping_mobile,
        invoiceDetails.shipping_address,
        invoiceDetails.shipping_landmark,
        invoiceDetails.customer_email
    ].filter(Boolean).join('\n');

    const billedFrom = [
        process.env.REACT_APP_COMPANY_NAME || "Grace Super Market",
        invoiceDetails.branch_address,
        invoiceDetails.branch_area,
        invoiceDetails.branch_city,
        `Pincode: ${invoiceDetails.branch_pincode}`
    ].filter(Boolean).join('\n');

    const usableWidth = pageWidth - margin * 2;
    const equalWidth = usableWidth / 2;

    doc.autoTable({
        startY: 100,
        head: [['Billed To:', 'Billed From:']],
        body: [[billedTo, billedFrom]],
        styles: { fontSize: 10, cellPadding: 4, valign: 'top' },
        headStyles: { fontStyle: 'bold' },
        columnStyles: {
            0: { cellWidth: equalWidth, halign: 'left' },
            1: { cellWidth: equalWidth, halign: 'left' }
        },
        margin: { left: 40, right: 40 }
    });

    let y = doc.lastAutoTable.finalY + 10;

    // 👇 New columns as per your request
    const tableColumn = ["DESCRIPTION", "MRP", "QTY", "AMOUNT", "DISCOUNT", "TOTAL AMOUNT"];
    const tableRows = invoiceDetails.products.map(item => {
        const quantity = item.quantity;
        const mrp = item.mrp;
        const salePrice = item.sale_price;
        const prodisc = item.orderlist_prodisc || 0;

        const totalMrp = mrp * quantity;
        const discount = prodisc > 0 ? prodisc * quantity : (mrp - salePrice) * quantity;
        const finalAmount = totalMrp - discount;

        return [
            `${item.name} ${item.unit} ${item.unit_type.toLowerCase()}`,
            mrp.toFixed(2),
            quantity.toString(),
            totalMrp.toFixed(2),
            discount > 0 ? discount.toFixed(2) : "-",
            finalAmount.toFixed(2)
        ];
    });

    doc.autoTable({
        head: [tableColumn],
        body: tableRows,
        theme: 'striped',
        styles: { fontSize: 9, cellPadding: 4 },
        headStyles: { fillColor: [0, 123, 255], textColor: 255 },
        startY: y + 5,
        margin: { left: 40, right: 40 },
    });
    const totalMrp = invoiceDetails.products.reduce(
        (sum, item) => sum + item.mrp * item.quantity,
        0
    );

    const totalDiscount = invoiceDetails.products.reduce((sum, item) => {
        const specialDiscount = item.orderlist_prodisc || 0;
        const isSpecial = specialDiscount > 0;
        const discount = isSpecial
            ? specialDiscount * item.quantity
            : (item.mrp - item.sale_price) * item.quantity;
        return sum + discount;
    }, 0);

    const deliveryCharge = Number(invoiceDetails.order_dlvrycharge || 0);
    const itemsNetAmount = totalMrp - totalDiscount;
    const totalFinalAmount = itemsNetAmount + deliveryCharge;

    const finalY = doc.lastAutoTable.finalY || (y + 100);
    doc.setFont("helvetica", "bold");
    doc.text(`Sub TOTAL: INR ${totalMrp.toFixed(2)}`, 360, finalY + 20);
    doc.text(`Total Discount: INR ${totalDiscount.toFixed(2)}`, 360, finalY + 40);

    let nextLineY = finalY + 60;
    if (deliveryCharge > 0) {
        doc.text(`Delivery Charge: INR ${deliveryCharge.toFixed(2)}`, 360, nextLineY);
        nextLineY += 20;
    }

    doc.text(`Total Amount: INR ${totalFinalAmount.toFixed(2)}`, 360, nextLineY);

    doc.setFontSize(10);
    doc.text("Thank you for your purchase!", 40, doc.internal.pageSize.height - 40);

    const filename = `GSM_${invoiceDetails.order_showid}.pdf`;
    doc.save(filename);
}

function PublicInvoice() {
    const { id } = useParams();
    const [isLoading, setIsLoading] = useState(true);
    const [invoiceDetails, setInvoiceDetails] = useState(null);
    useEffect(() => {
        setIsLoading(true);
        const fetchOrderDetail = async () => {
            try {
                const data = await omsApi.getCommonOrderById(id);
                if (data) {
                    setInvoiceDetails(data.data[0]);
                    setIsLoading(false);
                }
            } catch (e) {
                console.error("Failed to fetch branch:", e);
            }
        };
        fetchOrderDetail();
    }, []);
    const indCurrency = "₹";

    const totalMrp =
        invoiceDetails?.products?.reduce((sum, item) => sum + item.mrp * item.quantity, 0) || 0;

    const totalDiscount =
        invoiceDetails?.products?.reduce((sum, item) => {
            const specialDiscount = item.orderlist_prodisc || 0;
            const isSpecial = specialDiscount > 0;
            const discount = isSpecial
                ? specialDiscount * item.quantity
                : (item.mrp - item.sale_price) * item.quantity;
            return sum + discount;
        }, 0) || 0;

    const deliveryCharge = Number(invoiceDetails?.order_dlvrycharge || 0);
    const itemsNetAmount = totalMrp - totalDiscount;
    const totalFinalAmount = itemsNetAmount + deliveryCharge;
    return (
        <div>
            <Modal show={true} size="lg" centered>
                {isLoading || !invoiceDetails ? (
                    "Order detail is loading...."
                ) : (
                    <>
                        <div id="invoiceCapture">
                            <div className="d-flex flex-row justify-content-between align-items-start bg-light w-100 p-4">
                                <div className="w-100">
                                    <h4 className="fw-bold my-2">
                                        {invoiceDetails.customer_name}
                                    </h4>
                                    <h6 className="fw-bold text-secondary mb-1">
                                        Invoice #: {invoiceDetails.order_showid || ""}
                                    </h6>
                                    <h6 className="fw-bold text-secondary mb-1">

                                        {moment(invoiceDetails.order_posteddate).format(
                                            "YYYY/MM/DD kk:mm:ss"
                                        ) || ""}

                                    </h6>
                                </div>
                                <div className="text-end ms-4">
                                    <h6 className="fw-bold mt-1 mb-2">Amount&nbsp;:</h6>
                                    <h5 className="fw-bold text-secondary">
                                        {" "}
                                        {indCurrency} {totalFinalAmount.toFixed(2)}
                                    </h5>
                                </div>
                            </div>
                            <div className="p-4">
                                <Row className="mb-4">
                                    <Col md={6}>
                                        <div className="fw-bold">Billed to:</div>
                                        <div>{invoiceDetails.shipping_name || ""}</div>
                                        <div>{invoiceDetails.shipping_mobile || ""}</div>
                                        <div>{invoiceDetails.shipping_address || ""}</div>
                                        <div>{invoiceDetails.shipping_landmark || ""}</div>
                                        <div>{invoiceDetails.customer_email || ""}</div>
                                    </Col>
                                    <Col md={6}>
                                        <div className="fw-bold">Billed From:</div>
                                        <div>{process.env.REACT_APP_COMPANY_NAME || "Grace Super Market"}</div>
                                        <div>{invoiceDetails.branch_address || ''}</div>
                                        <div>{invoiceDetails.branch_area || ''}</div>
                                        <div>{invoiceDetails.branch_city || ''}</div>
                                        <div>pincode {invoiceDetails.branch_pincode || ''}</div>
                                    </Col>
                                </Row>
                                <Table className="mb-0">
                                    <thead>
                                        <tr>
                                            <th>DESCRIPTION</th>
                                            <th className="text-end">MRP</th>
                                            <th>QTY</th>
                                            <th className="text-end">AMOUNT</th>
                                            <th className="text-end">DISCOUNT</th>
                                            <th className="text-end">TOTAL AMOUNT</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {invoiceDetails.products.map((item, i) => {
                                            const quantity = item.quantity;
                                            const mrpAmount = item.mrp * quantity;
                                            const specialDiscount = item.orderlist_prodisc * quantity || 0;

                                            const isSpecial = specialDiscount > 0;
                                            const discount = isSpecial
                                                ? specialDiscount
                                                : (item.mrp - item.sale_price) * quantity;

                                            const finalAmount = mrpAmount - discount;

                                            return (
                                                <tr key={i}>
                                                    <td>
                                                        {item.name}
                                                        <span className="text-secondary  text-xs">
                                                            &nbsp; {item.unit} {item.unit_type.toLowerCase()}
                                                        </span>
                                                    </td>
                                                    <td className="text-end" style={{ width: "100px" }}>
                                                        {indCurrency} {item.mrp.toFixed(2)}
                                                    </td>
                                                    <td style={{ width: "70px" }}>{quantity}</td>
                                                    <td className="text-end" style={{ width: "100px" }}>
                                                        {indCurrency} {mrpAmount.toFixed(2)}
                                                    </td>
                                                    <td className="text-end" style={{ width: "100px" }}>
                                                        {discount > 0 ? `${indCurrency} ${discount.toFixed(2)}` : "-"}
                                                    </td>
                                                    <td className="text-end" style={{ width: "100px" }}>
                                                        {indCurrency} {finalAmount.toFixed(2)}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                                <Table>
                                    <tbody>
                                        <tr>
                                            <td colSpan={6}>&nbsp;</td>
                                        </tr>
                                        <tr className="text-end">
                                            <td></td>
                                            <td className="fw-bold" colSpan={4} style={{ width: "200px" }}>
                                                SUB TOTAL
                                            </td>
                                            <td className="text-end" style={{ width: "100px" }}>
                                                {indCurrency}{" "}
                                                {totalMrp.toFixed(2)}
                                            </td>
                                        </tr>

                                        <tr className="text-end">
                                            <td></td>
                                            <td className="fw-bold" colSpan={4} style={{ width: "200px" }}>
                                                TOTAL DISCOUNT
                                            </td>
                                            <td className="text-end" style={{ width: "100px" }}>
                                                {indCurrency}{" "}
                                                {totalDiscount.toFixed(2)}
                                            </td>
                                        </tr>
                                        {deliveryCharge > 0 && (
                                            <tr className="text-end">
                                                <td></td>
                                                <td className="fw-bold" colSpan={4} style={{ width: "200px" }}>
                                                    DELIVERY CHARGE
                                                </td>
                                                <td className="text-end" style={{ width: "100px" }}>
                                                    {indCurrency} {deliveryCharge.toFixed(2)}
                                                </td>
                                            </tr>
                                        )}

                                        <tr className="text-end">
                                            <td></td>
                                            <td className="fw-bold" colSpan={4} style={{ width: "200px" }}>
                                                TOTAL AMOUNT
                                            </td>
                                            <td className="text-end" style={{ width: "100px" }}>
                                                {indCurrency} {totalFinalAmount.toFixed(2)}
                                            </td>
                                        </tr>
                                    </tbody>
                                </Table>
                                {invoiceDetails.notes && (
                                    <div className="bg-light py-3 px-4 rounded">
                                        {invoiceDetails.notes}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="pb-4 px-4">
                            <Row>

                                <Col md={6}>
                                    <Button
                                        variant="outline-primary"
                                        className="d-block w-100 mt-3 mt-md-0"
                                        onClick={() => GenerateInvoice(invoiceDetails)}
                                    >
                                        <BiCloudDownload
                                            style={{
                                                width: "16px",
                                                height: "16px",
                                                marginTop: "-3px",
                                            }}
                                            className="me-2"
                                        />
                                        Download Copy
                                    </Button>
                                </Col>
                            </Row>
                        </div>
                    </>
                )}
            </Modal>
            <hr className="mt-4 mb-3" />
        </div>
    );
}

export default PublicInvoice;